'use client';

import {
  LICENSE_TYPE_TABS,
  LicenseTypeLabel,
  RECOMMENDED_PLANS,
  RecommendedPlan,
} from '@/data/branch/recommended-plans.data';
import { RECOMMEND_ENTRY_SOURCE } from '@/lib/recommended-store';
import { cn, formatNumber } from '@/lib/utils';
import { usePricingList } from '@/services/query/pricing.query';
import {
  CategoryDto,
  PriceDto,
  TAGS_SEPARATOR,
} from '@/services/types/pricing.type';
import { StoreEntity } from '@/services/types/store.type';
import { useModalStore } from '@/store/modal.store';
import Link from 'next/link';
import { useId, useState } from 'react';

interface Props {
  store: StoreEntity;
  /** 배치 위치별 여백 조정 */
  className?: string;
}

/**
 * 추천 요금제에 해당하는 API 요금제의 태그와, 선택한 면허 종류의 가격 1건.
 * 요금제·가격이 없으면 price는 undefined(가격 대신 상담 안내), 태그가 없으면 빈 배열.
 */
function findPricing(
  categories: CategoryDto[] | undefined,
  plan: RecommendedPlan,
  licenseLabel: LicenseTypeLabel,
): { planId?: number; tags: string[]; price?: PriceDto } {
  const matched = categories
    ?.flatMap((category) => category.plans ?? [])
    .find((item) => item.code === plan.planCode);
  // 요금안내 페이지와 같은 방식으로 API tags(CSV)를 그대로 쓴다.
  // API에 태그가 없는 요금제(1시간·2시간 이용반)는 추천 요금제 데이터의 기본 태그를 쓴다.
  const apiTags = matched?.tags
    ? matched.tags
        .split(TAGS_SEPARATOR)
        .map((tag) => tag.trim())
        .filter(Boolean)
    : [];
  const tags = apiTags.length > 0 ? apiTags : (plan.tags ?? []);
  const price = matched?.prices?.find(
    (item) => item.label?.trim() === licenseLabel,
  );
  return { planId: matched?.id, tags, price };
}

function PlanCard({
  index,
  plan,
  tags,
  price,
  licenseLabel,
  onReserve,
}: {
  index: number;
  plan: RecommendedPlan;
  tags: string[];
  price?: PriceDto;
  licenseLabel: LicenseTypeLabel;
  onReserve: () => void;
}) {
  const hasDiscount =
    !!price && price.originPrice > 0 && price.originPrice > price.price;
  const discountRate = hasDiscount
    ? Math.round((1 - price.price / price.originPrice) * 100)
    : 0;

  return (
    <li
      className={cn(
        'desktop:p-6 rounded-xl border p-5 shadow-[0_4px_14px_rgba(18,25,44,0.06)] transition',
        plan.isBest
          ? 'border-main-yellow/70 hover:border-main-yellow bg-[#fffbea]'
          : 'hover:border-main-blue/30 border-white bg-white',
      )}
    >
      <div className="flex items-center gap-2">
        <span className="bg-main-blue typo-13 flex h-6 min-w-8 items-center justify-center rounded-md px-1.5 font-black text-white">
          {String(index + 1).padStart(2, '0')}
        </span>
        <p className="typo-15 desktop:typo-16 text-main-blue leading-snug font-bold">
          {plan.target}
        </p>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <h3 className="typo-22 desktop:typo-24 text-level-1 font-extrabold">
          {plan.title}
        </h3>
        {plan.isBest && (
          <span className="bg-main-yellow text-level-1 typo-13 rounded-full px-2.5 py-1 leading-none font-black tracking-wide">
            BEST
          </span>
        )}
      </div>
      <p className="typo-15 desktop:typo-16 text-level-4 mt-2 leading-snug font-semibold">
        {plan.description}
      </p>

      {/* 태그: 요금안내 페이지 요금제 카드와 같은 마크업 */}
      {tags.length > 0 && (
        <div className="desktop:gap-2 mt-3 flex flex-wrap gap-1">
          {tags.map((tag) => (
            <div
              key={tag}
              className="bg-bg-grayblue desktop:h-10 flex h-[35px] items-center justify-center rounded-lg"
            >
              <span className="typo-15 text-font-grayblue desktop:typo-16 px-4 text-center font-semibold">
                {tag}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="border-stroke-grayblue mt-4 flex flex-wrap items-end justify-between gap-3 border-t pt-4">
        {price ? (
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="typo-13 text-level-5 font-semibold">
                {licenseLabel}
              </span>
              {hasDiscount && (
                <>
                  <span className="typo-14 text-level-6 font-semibold line-through">
                    {formatNumber(price.originPrice)}원
                  </span>
                  <span className="bg-main-orange/10 text-main-orange typo-13 rounded-sm px-1.5 py-1 font-extrabold">
                    {discountRate}% 할인
                  </span>
                </>
              )}
            </div>
            <span className="typo-26 desktop:typo-28 text-deepblue font-black">
              {formatNumber(price.price)}원
            </span>
          </div>
        ) : (
          <span className="typo-18 text-level-3 font-bold">
            요금은 상담 시 안내해 드려요
          </span>
        )}

        <button
          type="button"
          onClick={onReserve}
          className="bg-main-orange typo-16 flex h-11 cursor-pointer items-center gap-1.5 rounded-full px-5 font-extrabold text-white shadow-[0_6px_14px_rgba(255,85,0,0.28)] transition hover:bg-[#e64b00]"
        >
          이 요금제로 상담 예약
          <span aria-hidden>→</span>
        </button>
      </div>
    </li>
  );
}

/**
 * 학원 가기 전 추천 요금제(2종).
 * 학원 목록 추천 배너로 들어온 경우의 전용 배치(page.tsx 의 isRecommendEntry)에서만 렌더된다.
 * 면허 요금은 종류별로 가격이 달라 탭으로 고른다(기본 2종 보통 자동).
 */
export default function RecommendedPlansSection({ store, className }: Props) {
  const { open: openModal } = useModalStore();
  const { data } = usePricingList();
  const categories = data?.data;
  const titleId = useId();
  const [licenseLabel, setLicenseLabel] = useState<LicenseTypeLabel>(
    LICENSE_TYPE_TABS[0],
  );

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        'desktop:mt-9 desktop:p-7 mt-7 rounded-2xl border border-[#d9e7fb] bg-[#eef5ff] p-5',
        className,
      )}
    >
      <span className="bg-main-blue typo-14 inline-flex rounded-md px-2.5 py-1.5 font-black text-white">
        학원 가기 전 추천 요금제
      </span>
      <h2
        id={titleId}
        className="typo-24 desktop:typo-28 text-level-1 mt-3 leading-snug font-extrabold"
      >
        학원에서 부족한 연습, 시뮬레이터로 채우세요
      </h2>
      <p className="typo-15 desktop:typo-17 text-level-4 mt-2 leading-snug font-semibold">
        {store.name}에서 학원 등록 전에 감을 잡고, 부족한 연습은 원하는 시간에
        채워 보세요.
      </p>

      {/* 면허 종류 탭 */}
      <div
        role="tablist"
        aria-label="면허 종류"
        className="mt-5 flex flex-wrap gap-2"
      >
        {LICENSE_TYPE_TABS.map((label) => {
          const selected = label === licenseLabel;
          return (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setLicenseLabel(label)}
              className={cn(
                'typo-14 desktop:typo-15 h-10 cursor-pointer rounded-full border px-4 font-bold transition',
                selected
                  ? 'bg-main-blue border-main-blue text-white shadow-[0_4px_10px_rgba(20,116,250,0.25)]'
                  : 'text-level-3 hover:border-main-blue/40 border-[#d9e7fb] bg-white',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>

      <ol className="mt-4 flex flex-col gap-3">
        {RECOMMENDED_PLANS.map((plan, index) => {
          const { planId, tags, price } = findPricing(
            categories,
            plan,
            licenseLabel,
          );
          return (
            <PlanCard
              key={plan.planCode}
              index={index}
              plan={plan}
              tags={tags}
              price={price}
              licenseLabel={licenseLabel}
              // 선택한 요금제·면허 종류를 상담예약에 함께 남긴다(store_reserve.pricing_*)
              onReserve={() =>
                openModal(store, {
                  pricingPlanId: planId,
                  pricingPlanPriceId: price?.id,
                  entrySource: RECOMMEND_ENTRY_SOURCE,
                  pricingLabel: `${plan.title} · ${licenseLabel}`,
                  consultType: licenseLabel.startsWith('1종') ? '1종' : '2종',
                })
              }
            />
          );
        })}
      </ol>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <p className="typo-14 text-level-4 leading-snug font-semibold">
          요금은 면허 종류에 따라 달라요.
        </p>
        <Link
          href="/pricing"
          className="typo-14 text-main-blue font-bold hover:underline"
        >
          전체 요금 보기 →
        </Link>
      </div>
    </section>
  );
}
